import React, { useEffect } from 'react';
import { SectionWrapper } from './SectionWrapper';
import { Button } from './Button';
import { ShieldCheck, Clock, ArrowRight, Activity, Mail, Phone } from 'lucide-react';

interface HeroProps {
    onLeadSubmit: (email: string) => void;
    onStartCheck: () => void;
}

const Hero: React.FC<HeroProps> = ({ onLeadSubmit, onStartCheck }) => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = '//js-eu1.hsforms.net/forms/embed/v2.js';
    script.charset = 'utf-8';
    script.type = 'text/javascript';
    script.async = true;

    script.onload = () => {
      // @ts-ignore
      if (window.hbspt) {
        // @ts-ignore
        window.hbspt.forms.create({
          portalId: "147631815",
          formId: "68f15d73-c3d2-4e11-8b80-3009c816cdba",
          region: "eu1",
          target: "#hubspot-form-container"
        });
      }
    };

    document.body.appendChild(script);

    return () => {
      const hsScript = document.querySelector('script[src="//js-eu1.hsforms.net/forms/embed/v2.js"]');
      if (hsScript) {
        document.body.removeChild(hsScript);
      }
    };
  }, []);

  return (
    <SectionWrapper className="pt-24 pb-10 md:pt-40 md:pb-28 overflow-x-hidden">
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-20 items-start">
        
        <div className="flex-1 text-left w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-surface border border-divider text-[10px] md:text-sm font-bold text-accent mb-4 md:mb-6 shadow-sm tracking-wider">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
            </span>
            ROMBUS – IT-SCHUTZ AM BAU
          </div>

          <h1 className="text-3xl md:text-6xl font-black leading-[1.1] mb-4 md:mb-8 text-primary tracking-tight">
            Kein Baustopp.<br />
            <span className="text-accent">Keine Haftungsfalle.</span>
          </h1>
          
          <p className="text-sm md:text-xl mb-6 md:mb-10 leading-relaxed text-gray-600 font-light max-w-2xl">
            <span className="font-semibold text-primary">Für Bauleiter & Projektleiter:</span> Schützen Sie aktive IT-Systeme während der Bauphase – sicher und gemäss <span className="font-semibold">SIA-kompatibler Vorgehensweise</span>.
          </p>

          <div className="mb-8 p-4 md:p-6 bg-surface border border-divider rounded-2xl flex flex-row items-center gap-4 max-w-lg shadow-sm">
            <div className="w-14 h-14 md:w-24 md:h-24 rounded-full bg-gray-100 border border-divider overflow-hidden shrink-0 shadow-inner">
              <img 
                src="https://raw.githubusercontent.com/atedo-ch/rombus/main/markus-renggli-1024x1024.png" 
                alt="Markus Renggli, rombus ag" 
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.src = "https://ui-avatars.com/api/?name=Markus+Renggli&background=487274&color=fff"; }}
              />
            </div>
            <div className="text-left">
              <p className="text-base md:text-lg font-bold text-primary mb-0.5 leading-tight">Markus Renggli</p>
              <p className="text-[10px] md:text-sm text-gray-500 font-medium mb-1.5 leading-tight uppercase tracking-wide">Geschäftsführer rombus ag</p>
              <div className="flex flex-col gap-1">
                 <a href="mailto:markus.renggli@rombus-experts.ch" className="text-[10px] md:text-sm text-accent hover:underline flex items-center gap-2 font-bold truncate">
                    <Mail className="w-3 h-3 shrink-0" /> markus.renggli@rombus-experts.ch
                 </a>
                 <a href="tel:+41413104510" className="text-[10px] md:text-sm text-gray-400 flex items-center gap-2 font-medium">
                    <Phone className="w-3 h-3 shrink-0" /> +41 41 310 45 10
                 </a>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <Button 
              variant="secondary" 
              onClick={onStartCheck}
              className="group flex items-center justify-center gap-3 bg-surface border-divider hover:border-accent hover:bg-white text-accent transition-all shadow-sm active:scale-95 py-4 w-full sm:w-auto"
            >
              <Activity className="w-5 h-5" />
              <span className="text-sm md:text-base font-bold">IT-Risiko Analyse starten</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>

          <div className="flex flex-col gap-3 text-[11px] md:text-sm font-semibold">
              <div className="flex items-center gap-3">
                  <ShieldCheck className="w-4 h-4 md:w-5 md:h-5 text-accent shrink-0" />
                  <span className="text-accent">Spezialist für «Running Systems» seit 33 Jahren</span>
              </div>
              <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 md:w-5 md:h-5 text-accent shrink-0" />
                  <span className="text-gray-500">Kostenlose Prüfung in unter 5 Min.</span>
              </div>
          </div>
        </div>

        <div className="w-full lg:w-[400px] shrink-0 mt-6 lg:mt-0">
          <div className="bg-accent p-6 md:p-8 rounded-2xl md:rounded-3xl border border-white/10 shadow-2xl relative overflow-hidden">
            <h3 className="text-base md:text-xl font-bold text-white mb-6 text-center leading-tight">ICT-Risiko-Leitfaden (PDF)</h3>
            
            <div className="mb-6 relative group max-w-[200px] md:max-w-[280px] mx-auto lg:max-w-none">
              <div className="relative w-full aspect-[3/4] bg-accent/20 rounded-lg overflow-hidden shadow-lg flex items-center justify-center border border-white/20">
                <img 
                  src="https://raw.githubusercontent.com/atedo-ch/rombus/main/300x484_Display-Whitepaper.png" 
                  alt="ICT-Risiko-Leitfaden Whitepaper" 
                  className="w-full h-full object-cover transition-transform duration-700"
                />
              </div>
              <div className="absolute -bottom-2 -right-2 bg-[#75C5BE] text-white px-3 py-1.5 rounded-lg shadow-xl -rotate-3 z-10 border-2 border-accent">
                <span className="text-[9px] md:text-xs font-black uppercase tracking-widest">Kostenlos</span>
              </div>
            </div>

            <div id="hubspot-form-container" className="hubspot-form-wrapper">
                <div className="flex flex-col items-center justify-center py-4 text-white/50 animate-pulse">
                    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin mb-2"></div>
                    <p className="text-[9px] font-bold uppercase tracking-widest">Wird geladen...</p>
                </div>
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        .hubspot-form-wrapper form {
          background: transparent !important;
        }
        .hubspot-form-wrapper label {
          color: rgba(255,255,255,0.9) !important;
          font-size: 10px !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          margin-bottom: 4px !important;
          display: block !important;
        }
        .hubspot-form-wrapper input[type="text"],
        .hubspot-form-wrapper input[type="email"],
        .hubspot-form-wrapper input[type="tel"],
        .hubspot-form-wrapper select {
          border-radius: 0.5rem !important;
          padding: 0.6rem 0.75rem !important;
          background: #FFFFFF !important;
          font-size: 13px !important;
          margin-bottom: 8px !important;
          border: none !important;
        }
        .hubspot-form-wrapper .hs-button {
          background: #FFFFFF !important;
          color: #487274 !important;
          border-radius: 0.5rem !important;
          font-weight: 900 !important;
          padding: 0.85rem !important;
          width: 100% !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
          font-size: 13px !important;
          box-shadow: 0 4px 10px rgba(0,0,0,0.1) !important;
          border: none !important;
          cursor: pointer !important;
        }
      `}</style>
    </SectionWrapper>
  );
};

export default Hero;